/**
 * Hook pentru verificarea permisiilor în componente React
 * 
 * Utilizare:
 *   const { hasPermission, isLoading } = usePermission()
 *   
 *   // Verificare simplă
 *   if (hasPermission('studies:view')) { ... }
 *   
 *   // Verificare multiplă (oricare)
 *   if (hasPermission(['studies:view', 'studies:edit'])) { ... }
 *   
 *   // Verificare toate
 *   if (hasAllPermissions(['studies:view', 'reports:create'])) { ... }
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { permissionsService, UserPermissions } from '../services/permissionsApi'

// Cache global pentru permisiuni (evită request-uri multiple)
let permissionsCache: UserPermissions | null = null
let cacheTimestamp: number = 0
const CACHE_TTL = 5 * 60 * 1000 // 5 minute

// Rolul admin are wildcard
const ADMIN_ROLE = 'admin'
const WILDCARD_PERMISSION = '*:*'

interface UsePermissionResult {
  /** Verifică dacă utilizatorul are permisiunea (sau oricare din lista) */
  hasPermission: (permission: string | string[]) => boolean
  
  /** Verifică dacă utilizatorul are TOATE permisiunile din listă */
  hasAllPermissions: (permissions: string[]) => boolean
  
  /** Lista permisiunilor efective */
  permissions: string[]
  
  /** Rolul utilizatorului */
  userRole: string
  
  /** Loading state */
  isLoading: boolean
  
  /** Eroare (dacă există) */
  error: string | null
  
  /** Reîncarcă permisiunile */
  refreshPermissions: () => Promise<void>
}

export function usePermission(): UsePermissionResult {
  const { user, isAuthenticated } = useAuth()
  const [userPermissions, setUserPermissions] = useState<UserPermissions | null>(permissionsCache)
  const [isLoading, setIsLoading] = useState(!permissionsCache)
  const [error, setError] = useState<string | null>(null)

  // Încarcă permisiunile din API
  const loadPermissions = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setUserPermissions(null)
      setIsLoading(false)
      return
    }

    // Verifică cache-ul
    const now = Date.now()
    if (permissionsCache && (now - cacheTimestamp) < CACHE_TTL) {
      setUserPermissions(permissionsCache)
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      
      const perms = await permissionsService.getMyPermissions()
      
      // Actualizează cache-ul
      permissionsCache = perms
      cacheTimestamp = now
      
      setUserPermissions(perms)
    } catch (err: any) {
      console.error('Eroare la încărcarea permisiunilor:', err)
      setError(err.message || 'Eroare la încărcarea permisiunilor')
      
      // Fallback: folosește rolul pentru permisiuni de bază
      if (user) {
        setUserPermissions({
          user_id: user.id,
          user_email: user.email,
          user_role: user.role,
          role_permissions: [],
          granted_overrides: [],
          revoked_overrides: [],
          effective_permissions: user.role === ADMIN_ROLE ? [WILDCARD_PERMISSION] : [],
        })
      }
    } finally {
      setIsLoading(false)
    }
  }, [isAuthenticated, user])

  // Încarcă permisiunile la mount și când se schimbă utilizatorul
  useEffect(() => {
    loadPermissions()
  }, [loadPermissions])

  // Invalidează cache-ul când se schimbă utilizatorul
  useEffect(() => {
    if (!isAuthenticated) {
      permissionsCache = null
      cacheTimestamp = 0
    }
  }, [isAuthenticated])

  // Funcție pentru verificarea unei permisiuni
  const hasPermission = useCallback((permission: string | string[]): boolean => {
    // Admin are tot
    if (user?.role === ADMIN_ROLE) {
      return true
    }

    if (!userPermissions) {
      return false
    }

    const effectivePerms = userPermissions.effective_permissions

    // Verifică wildcard
    if (effectivePerms.includes(WILDCARD_PERMISSION)) {
      return true
    }

    // Dacă e array, verifică oricare
    if (Array.isArray(permission)) {
      return permission.some(p => {
        if (effectivePerms.includes(p)) return true
        // Verifică și wildcard pe resursă (ex: studies:*)
        const resource = p.split(':')[0]
        return effectivePerms.includes(`${resource}:*`)
      })
    }

    // Permisiune singulară
    if (effectivePerms.includes(permission)) {
      return true
    }

    // Verifică wildcard pe resursă
    const resource = permission.split(':')[0]
    return effectivePerms.includes(`${resource}:*`)
  }, [user?.role, userPermissions])

  // Funcție pentru verificarea tuturor permisiunilor
  const hasAllPermissions = useCallback((permissions: string[]): boolean => {
    // Admin are tot
    if (user?.role === ADMIN_ROLE) {
      return true
    }

    return permissions.every(p => hasPermission(p))
  }, [user?.role, hasPermission])

  // Refresh manual
  const refreshPermissions = useCallback(async () => {
    permissionsCache = null
    cacheTimestamp = 0
    await loadPermissions()
  }, [loadPermissions])

  // Memoizează lista de permisiuni
  const permissions = useMemo(() => {
    return userPermissions?.effective_permissions || []
  }, [userPermissions])

  return {
    hasPermission,
    hasAllPermissions,
    permissions,
    userRole: user?.role || '',
    isLoading,
    error,
    refreshPermissions,
  }
}

/**
 * Hook simplificat pentru verificare rapidă
 * 
 * Utilizare:
 *   const canViewStudies = useHasPermission('studies:view')
 */
export function useHasPermission(permission: string | string[]): boolean {
  const { hasPermission, isLoading } = usePermission()
  
  // În timpul încărcării, returnează false pentru siguranță
  if (isLoading) {
    return false
  }
  
  return hasPermission(permission)
}

/**
 * Invalidează cache-ul de permisiuni (util după modificări)
 */
export function invalidatePermissionsCache(): void {
  permissionsCache = null
  cacheTimestamp = 0
}

export default usePermission

